import React from 'react';
import {
  Box, Typography, Chip, Button, CircularProgress, Alert, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import { FlaskConical, ExternalLink, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, inpatient } from '../../lib/api';
import { toast } from 'react-hot-toast';
import { useWebSocketContext } from '../../contexts/WebSocketContext';

/** Trend chart for one formal LabTestParameter, fetched on demand when a value chip is clicked. */
function ParameterTrendDialog({ open, onClose, parameterId, parameterName, patientId }: {
  open: boolean; onClose: () => void; parameterId: string | number | null; parameterName: string; patientId: string;
}) {
  const theme = useTheme();
  const { data, isLoading } = useQuery({
    queryKey: ['param-trend', patientId, parameterId],
    queryFn: async () => {
      const res = await inpatient.getParameterTrend(parameterId as string | number, patientId);
      return (res as any)?.data ?? [];
    },
    enabled: open && !!parameterId,
  });

  const points = (Array.isArray(data) ? data : [])
    .filter((p: any) => p.numericValue != null)
    .map((p: any) => ({ date: p.recordedAt, value: p.numericValue, unit: p.unit }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{parameterName} — Trend</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : points.length < 2 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Not enough historical values yet — at least 2 results for this parameter are needed to chart a trend.
          </Typography>
        ) : (
          <Box sx={{ height: 260, width: '100%', mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }}
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3], backgroundColor: theme.palette.background.paper }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString('en-IN')}
                  formatter={(v: any) => [`${v} ${points[0]?.unit || ''}`, parameterName]} />
                <Line type="monotone" dataKey="value" stroke={theme.palette.primary.main} strokeWidth={2}
                  dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

const STATUS_CONFIG: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'default' | 'error' }> = {
  ORDERED:      { label: 'Pending',     color: 'warning' },
  ACKNOWLEDGED: { label: 'Received',    color: 'info' },
  IN_PROGRESS:  { label: 'In Progress', color: 'info' },
  COMPLETED:    { label: 'Completed',   color: 'success' },
  REVIEWED:     { label: 'Reviewed',    color: 'success' },
  CANCELLED:    { label: 'Cancelled',   color: 'error' },
};

function formatDate(val: string | null | undefined) {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return val; }
}

function ViewDocButton({ filePath, fileName }: { filePath: string; fileName: string }) {
  const [loading, setLoading] = useState(false);

  const handleView = async () => {
    setLoading(true);
    try {
      const res = await api.labUpload.getPreviewUrl(filePath);
      const url = res?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else toast.error('Preview not available');
    } catch {
      toast.error('Could not load preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={loading ? <CircularProgress size={13} /> : <ExternalLink size={13} />}
      onClick={handleView}
      disabled={loading}
      title={fileName}
      sx={{ borderRadius: 4 }}
    >
      View Report
    </Button>
  );
}

interface Props {
  patientId: string;
}

export function PatientLabOrdersSection({ patientId }: Props) {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocketContext();
  const [trendTarget, setTrendTarget] = useState<{ parameterId: string | number; parameterName: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['patient-lab-orders-staff', patientId],
    queryFn: async () => {
      const res = await api.patients.getLabOrders(patientId);
      return (res as any)?.data ?? [];
    },
    enabled: !!patientId,
  });

  // Refresh instantly when a lab document upload completes for this patient
  useEffect(() => {
    const unsub = subscribe('/user/queue/notifications', (msg) => {
      if (msg.type === 'LAB_UPLOAD_COMPLETE' && (msg as any).patientId === patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-lab-orders-staff', patientId] });
      }
    });
    return unsub;
  }, [subscribe, patientId, queryClient]);

  const orders: any[] = Array.isArray(data) ? data : (data as any)?.data ?? [];

  if (isLoading) return <CircularProgress size={20} />;
  if (error) return <Alert severity="error" sx={{ mt: 1 }}>Failed to load lab orders.</Alert>;

  if (orders.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No lab orders for this patient.</Typography>
      </Box>
    );
  }

  return (
    <>
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'action.hover' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Test</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Ordered On</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Report</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order: any) => {
            const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.ORDERED;
            const docs: any[] = order.documents ?? [];
            const values: any[] = order.parameterValues ?? [];
            return (
              <React.Fragment key={order.id}>
              <TableRow hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FlaskConical size={14} color="#1976d2" />
                    <Typography variant="body2" fontWeight={500}>{order.testName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(order.orderedAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={cfg.label}
                    color={cfg.color}
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell align="right">
                  {docs.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {docs.map((doc: any) => (
                        <ViewDocButton key={doc.id} filePath={doc.filePath} fileName={doc.fileName} />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">Awaiting upload</Typography>
                  )}
                </TableCell>
              </TableRow>
              {/* Structured parameter values — captured at result entry, now shown to outpatient doctors
                  with reference ranges + critical flags (previously only the raw PDF was visible). */}
              {values.length > 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 0.5, bgcolor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', px: 2, alignItems: 'center' }}>
                      {values.map((v: any, i: number) => {
                        const isModerate = !v.isCritical && v.severity === 'MODERATE';
                        return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Typography variant="caption"
                            sx={{ fontWeight: v.isCritical ? 800 : isModerate ? 700 : 500,
                                  color: v.isCritical ? 'error.main' : isModerate ? 'warning.main' : 'text.primary' }}>
                            {v.parameterName}: {v.resultValue}{v.unit ? ` ${v.unit}` : ''}
                            {v.referenceRange ? (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {' '}(ref {v.referenceRange})
                              </Typography>
                            ) : null}
                            {v.isCritical ? ' ⚠' : isModerate ? ' ↕' : ''}
                          </Typography>
                          {v.parameterId != null && (
                            <Box component="button" type="button" title={`View ${v.parameterName} trend`}
                              onClick={() => setTrendTarget({ parameterId: v.parameterId, parameterName: v.parameterName })}
                              sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', p: 0.25, borderRadius: 1, color: 'primary.main',
                                    '&:hover': { bgcolor: 'action.hover' } }}>
                              <TrendingUp size={13} />
                            </Box>
                          )}
                        </Box>
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    <ParameterTrendDialog
      open={!!trendTarget}
      onClose={() => setTrendTarget(null)}
      parameterId={trendTarget?.parameterId ?? null}
      parameterName={trendTarget?.parameterName ?? ''}
      patientId={patientId}
    />
    </>
  );
}
